import store from '@state/store'
import { BASE_PATH } from '@utils/constant'
import { FULL_ROLE } from '@utils/helpers'
import { i18n } from '@plugins/i18n'

// external file routes
import agentListRoutes from '@router/modules/agentList'
import agentAdminListRoutes from '@router/modules/adminListAgentAdmin'
import memberListRoutes from '@router/modules/memberList'
import measureListRoutes from '@router/modules/measureList'
import deviceListRoutes from '@router/modules/deviceList'
import adminListRoutes from '@router/modules/adminList'
import notificationRoutes from '@router/modules/notification'
import dashboardRoutes from '@router/modules/dashboard'
import friendListRoutes from '@router/modules/friendList'
import deviceUserRoutes from '@router/modules/deviceUserList'
import memberListAgentAdminRoutes from '@router/modules/memberListAgentAdmin'
import measureListAgentRoutes from '@router/modules/measureListAgent'
import deviceListAgentAdminRoutes from '@router/modules/deviceListAgentAdmin'
import notificationAgentAdminRoutes from '@router/modules/notificationAgentAdmin'
import settingRoutes from '@router/modules/setting'
import feedbackRoutes from '@router/modules/feedback'
import logRoutes from '@router/modules/log'
// external file routes end

export default [
  {
    path: '/panel/index.html',
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/loggedIn']) {
          // Redirect to the base page with role instead
          const currentUser = store.getters['auth/getCurrentUser']
          next({
            name: BASE_PATH[currentUser.level],
          })
        } else {
          // Continue to the login page
          next({ name: 'login' })
        }
      },
    },
  },
  {
    path: '/',
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/loggedIn']) {
          // Redirect to the base page with role instead
          const currentUser = store.getters['auth/getCurrentUser']
          next({
            name: BASE_PATH[currentUser.level],
          })
        } else {
          // Continue to the login page
          next({ name: 'login' })
        }
      },
    },
  },
  {
    path: '/admin/',
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/loggedIn']) {
          // Redirect to the base page with role instead
          const currentUser = store.getters['auth/getCurrentUser']
          next({
            name: BASE_PATH[currentUser.level],
          })
        } else {
          // Continue to the login page
          next({ name: 'login' })
        }
      },
    },
  },
  {
    path: '/alliance/:agent_code',
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        if (store.getters['auth/loggedIn']) {
          // Redirect to the base page with role instead
          const currentUser = store.getters['auth/getCurrentUser']
          next({
            name: BASE_PATH[currentUser.level],
          })
        } else {
          // Continue to the login page
          next({ name: 'login', query: routeTo.params })
        }
      },
    },
  },
  {
    path: '/panel',
    component: () => lazyLoadView(import('@layouts/main.vue')),
    meta: {
      label: i18n.t('router.home'),
    },
    redirect: (to) => {
      const currentUser = store.getters['auth/getCurrentUser']
      if (!currentUser) {
        return { name: 'login' }
      }
      return { name: BASE_PATH[currentUser.level] }
    },
    children: [
      agentListRoutes,
      agentAdminListRoutes,
      memberListRoutes,
      measureListRoutes,
      deviceListRoutes,
      adminListRoutes,
      notificationRoutes,
      dashboardRoutes,
      friendListRoutes,
      deviceUserRoutes,
      memberListAgentAdminRoutes,
      measureListAgentRoutes,
      deviceListAgentAdminRoutes,
      notificationAgentAdminRoutes,
      settingRoutes,
      feedbackRoutes,
      logRoutes,
      {
        path: 'logout',
        name: 'logout',
        meta: {
          authRequired: FULL_ROLE,
          beforeResolve(routeTo, routeFrom, next) {
            const currentUser = store.state.auth.currentUser
            const agentCode = currentUser?.agent?.agent_code
            const query = agentCode ? { agent_code: agentCode } : {}
            store.dispatch('auth/logOut')
            next({ name: 'login', query })
          },
        },
      },
    ],
  },
  {
    path: '/panel/login',
    component: () => lazyLoadView(import('@layouts/second-main.vue')),
    children: [
      {
        path: '',
        name: 'login',
        component: () => lazyLoadView(import('@views/login.vue')),
      },
    ],
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/_404.vue').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
export function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    // error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
